import { IconButton } from "$components/button/icon-button";
import { makeStyles } from "$components/mui-styles";
import RHFTextField from "$components/rhf/text-field";
import { Box, Grid, TableCell, TableRow } from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import classNames from "classnames";
import { format } from "date-fns";
import { type FC, useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useIntl } from "react-intl";
import useCurrencyFormatter from "../../../hooks/useCurrencyFormatter";

import type { LineItem, Payers, PriceList } from "../types";
import {
  COST,
  DISABLED,
  FORMATTED_PRICE,
  LINEITEMS,
  PRICE_LIST_ITEM,
  PRICE_TARIFF,
  QUANTITY,
  REVENUE_DEPARTMENT,
  SUPPLIER,
} from "./formConstants";
import { PriceListItemInput } from "./PriceListItemInput";
import { RevenueDepartmentSelectInput } from "./RevenueDepartmentSelectInput";
import { SupplierSelectInput } from "./SupplierSelectInput";
import { TariffSelectInput } from "./TariffSelectInput";

const useStyles = makeStyles((theme) => ({
  itemColumn: {
    width: "45%",
  },
  row: {
    "& td": {
      padding: theme.spacing(1, 0.5),
      verticalAlign: "top",
      borderBottom: "none",
    },
  },
  lineItemBorder: {
    borderBottom: `1px solid ${theme.palette.divider} !important`,
  },
  actions: {
    alignSelf: "center",
  },
  disabledCell: {
    opacity: 0.5,
    pointerEvents: "none",
  },
}));

export const EditableInvoiceLineItem: FC<{
  removeItem: (index: number) => void;
  lineItem: LineItem;
  index: number;
  priceList: PriceList;
}> = ({ removeItem, lineItem, index, priceList }) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const { setValue } = useFormContext();
  const { currencyFormatter } = useCurrencyFormatter();

  const isAnAutomatedLineItem = lineItem.automated;

  useEffect(() => {
    setValue(`${LINEITEMS}[${index}].${DISABLED}`, false, {
      shouldDirty: true,
    });
  }, [setValue, index]);

  const selectedPriceListItem =
    useWatch({ name: `${LINEITEMS}[${index}].${PRICE_LIST_ITEM}` }) ??
    lineItem?.priceListItem ??
    {};
  const selectedItemPayers = selectedPriceListItem?.payers;

  // Watch for changes in quantity and selected tariff to calculate prices
  const quantity =
    useWatch({ name: `${LINEITEMS}[${index}].${QUANTITY}` }) ??
    lineItem?.quantity ??
    1;
  const selectedTariffId =
    useWatch({
      name: `${LINEITEMS}[${index}].${PRICE_LIST_ITEM}.${PRICE_TARIFF}`,
    }) ?? selectedPriceListItem?.activeTariffId;

  // `activeTariffId` is used as an indicator for enabling the tariff dropdown
  // because it signifies the presence of a tariff associated with the selected item.
  const hasSelectedItemWithTariff = !!selectedPriceListItem?.activeTariffId;

  // selectedPayer is either the first one in the list or it is the one with the activeTariffId
  const selectedPayer =
    selectedItemPayers?.find(
      (payer: Payers) =>
        payer.tariffId === selectedPriceListItem.activeTariffId,
    ) ?? selectedItemPayers?.[0];

  // Calculate and set cost and formatted price when tariff or quantity changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: setValue and currencyFormatter cause infinite loops
  useEffect(() => {
    if (selectedItemPayers && quantity && selectedTariffId) {
      // Find the correct payer based on the selected tariff ID
      const currentSelectedPayer = selectedItemPayers.find(
        (payer: Payers) => payer.tariffId === selectedTariffId,
      );

      if (currentSelectedPayer) {
        const cost = currentSelectedPayer.amountInPence * quantity;
        const formattedPrice = currencyFormatter(cost);

        setValue(`${LINEITEMS}[${index}].${COST}`, cost, { shouldDirty: true });
        setValue(`${LINEITEMS}[${index}].${FORMATTED_PRICE}`, formattedPrice, {
          shouldDirty: true,
        });
      } else {
        // Clear values if no valid payer found
        setValue(`${LINEITEMS}[${index}].${COST}`, 0, { shouldDirty: true });
        setValue(`${LINEITEMS}[${index}].${FORMATTED_PRICE}`, "", {
          shouldDirty: true,
        });
      }
    } else {
      // Clear values if no valid selection
      setValue(`${LINEITEMS}[${index}].${COST}`, 0, { shouldDirty: true });
      setValue(`${LINEITEMS}[${index}].${FORMATTED_PRICE}`, "", {
        shouldDirty: true,
      });
    }
  }, [selectedItemPayers, quantity, selectedTariffId, index]);

  return (
    <TableRow className={classNames(classes.row, classes.lineItemBorder)}>
      <TableCell>
        {lineItem.incurredAt ? (
          <span title={lineItem.incurredAt.toString()}>
            {format(lineItem.incurredAt, "yyyy-MM-dd")}
          </span>
        ) : null}
      </TableCell>
      <TableCell className={classNames(classes.itemColumn)}>
        <PriceListItemInput
          name={`${LINEITEMS}[${index}].${PRICE_LIST_ITEM}` as const}
          priceListItems={priceList.priceListItems || []}
          disabled={isAnAutomatedLineItem}
        />
      </TableCell>
      <TableCell>
        <RevenueDepartmentSelectInput
          name={
            `${LINEITEMS}[${index}].${PRICE_LIST_ITEM}.${REVENUE_DEPARTMENT}` as const
          }
        />
      </TableCell>
      <TableCell>
        <SupplierSelectInput
          name={`${LINEITEMS}[${index}].${SUPPLIER}` as const}
        />
      </TableCell>
      <TableCell
        className={classNames({
          [classes.disabledCell]: isAnAutomatedLineItem,
        })}
      >
        <RHFTextField
          name={`${LINEITEMS}[${index}].${QUANTITY}` as const}
          type="number"
          required
          fullWidth
          InputProps={{
            inputProps: { min: 1, max: 1000 },
            "data-testid": `${LINEITEMS}[${index}].${QUANTITY}`,
          }}
          label={formatMessage({
            id: "invoicing.quantity.label",
            defaultMessage: "Qty",
          })}
          disabled={isAnAutomatedLineItem}
        />
      </TableCell>
      <TableCell>
        <Grid container>
          <Grid item>
            <TariffSelectInput
              name={
                `${LINEITEMS}[${index}].${PRICE_LIST_ITEM}.${PRICE_TARIFF}` as const
              }
              selectedTariff={selectedPayer?.tariffId}
              payers={selectedItemPayers}
              disabled={!hasSelectedItemWithTariff}
            />
          </Grid>
          <Grid item xs className={classes.actions}>
            <Box pl={2} textAlign="right">
              <IconButton
                color="error"
                onClick={() => removeItem(index)}
                aria-label={formatMessage({
                  id: "invoicing.lineItems.delete",
                  defaultMessage: "Remove item",
                })}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          </Grid>
        </Grid>
      </TableCell>
    </TableRow>
  );
};
